import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, Link } from 'react-router-dom';
import { TbArrowRight, TbTrendingUp, TbTrendingDown, TbCreditCard } from 'react-icons/tb';
import DualBarChart from './DualBarChart';
import Button from '../buttons/Button';
import * as bankAccountService from '../../../services/community/bankAccountService';
import { JOURNAL_ENTRY_SOURCE_TYPES } from '../../../types/bankAccount';
import useSelectedCommunity from '../../../hooks/community/useSelectedCommunity';
import { calculateDisplayAmount } from '../../../utils/bankAccountUtil';

interface IncomeExpensesChartProps {
  bankAccounts?: any[];
}

function IncomeExpensesChart({ bankAccounts = [] }: IncomeExpensesChartProps): JSX.Element {
  const { t, i18n } = useTranslation('', {
    keyPrefix: 'page.financial.overview.incomeExpensesChart',
  });
  const navigate = useNavigate();
  const community = useSelectedCommunity();

  const [loading, setLoading] = useState<boolean>(false);
  const [transactionsLoaded, setTransactionsLoaded] = useState<boolean>(false);
  const [chartData, setChartData] = useState<
    { date: string; income: number; expenses: number; label: string }[]
  >([]);

  // Mock income and expenses data generation - 12 months
  const monthSkeleton = useMemo(() => {
    const now = new Date();
    const formatLocale = i18n.language === 'nl' ? 'nl-NL' : 'en-US';
    const months: { key: string; label: string }[] = [];
    for (let i = 11; i >= 0; i -= 1) {
      const date = new Date(now);
      date.setDate(1);
      date.setHours(0, 0, 0, 0);
      date.setMonth(date.getMonth() - i);
      const key = `${date.getFullYear()}-${date.getMonth() + 1}`; // e.g., 2025-3
      const label = date.toLocaleDateString(formatLocale, { month: 'short', year: 'numeric' });
      months.push({ key, label });
    }
    return months;
  }, [i18n.language]);

  useEffect(() => {
    const load = async () => {
      if (!community || community.id == null) return;
      setLoading(true);
      try {
        const journalEntries = await bankAccountService.getAllJournalEntries();
        setTransactionsLoaded(true);

        // Initialize buckets for last 12 months
        const buckets = new Map<string, { income: number; expenses: number; label: string }>();
        monthSkeleton.forEach((m) => {
          buckets.set(m.key, { income: 0, expenses: 0, label: m.label });
        });

        journalEntries.forEach((entry) => {
          const dt = new Date(entry.entryDate);
          const key = `${dt.getFullYear()}-${dt.getMonth() + 1}`;
          if (!buckets.has(key)) return; // ignore entries older than 12 months

          const amount = calculateDisplayAmount(entry);

          if (
            entry.sourceType === JOURNAL_ENTRY_SOURCE_TYPES.GROUP_INCOME ||
            entry.sourceType === JOURNAL_ENTRY_SOURCE_TYPES.CONTRIBUTION_RUN
          ) {
            const bucket = buckets.get(key)!;
            bucket.income += amount > 0 ? amount : 0;
          } else if (entry.sourceType === JOURNAL_ENTRY_SOURCE_TYPES.GROUP_EXPENSE) {
            const bucket = buckets.get(key)!;
            bucket.expenses += amount < 0 ? Math.abs(amount) : 0;
          }
        });

        const data = monthSkeleton.map((m) => {
          const bucket = buckets.get(m.key)!;
          return {
            date: m.label,
            income: bucket.income / 100,
            expenses: bucket.expenses / 100,
            label: bucket.label,
          };
        });

        setChartData(data);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [community?.id, monthSkeleton]);

  const handleSeeMore = () => {
    navigate('/financial/reports', { state: { activeTab: 'profitLoss' } });
  };

  const hasAnyData = useMemo(() => {
    return chartData.some((d) => d.income > 0 || d.expenses > 0);
  }, [chartData]);

  return (
    <div className="rounded-xl bg-secondary-200 bg-opacity-50 p-6">
      {/* Header */}
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-primary-900">{t('title')}</h2>
          <p className="mt-1 text-sm text-gray-500">
            {t('subtitle', { period: t('periodLabels.monthly') })}
          </p>
        </div>
        <Link
          to="/financial/reports"
          state={{ activeTab: 'profitLoss' }}
          className="flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium text-primary-900 transition-colors hover:bg-primary-300 hover:bg-opacity-20">
          <span>{t('seeProfitLoss')}</span>
          <TbArrowRight size={16} />
        </Link>
      </div>

      {/* Body */}
      {bankAccounts.length === 0 || (!loading && transactionsLoaded && !hasAnyData) ? (
        <div className="py-12 text-center">
          <TbCreditCard className="mx-auto mb-4 h-12 w-12 text-primary-300" />
          {bankAccounts.length === 0 ? (
            <>
              <h3 className="mb-2 text-lg font-medium text-primary-900">{t('empty.title')}</h3>
              <p className="mb-6 text-sm text-gray-500">{t('empty.subtitle')}</p>
              <Button variant="primary-dark" onClick={() => navigate('/financial/transactions')}>
                {t('empty.cta')}
              </Button>
            </>
          ) : (
            <>
              <h3 className="mb-2 text-lg font-medium text-primary-900">
                {t('empty.noDataTitle', { defaultValue: 'No transactions or mutations yet' })}
              </h3>
              <p className="text-sm text-gray-500">
                {t('empty.noDataSubtitle', {
                  defaultValue:
                    'Once transactions are synced and processed, income and expenses will appear here.',
                })}
              </p>
            </>
          )}
        </div>
      ) : (
        <>
          {/* Legend */}
          <div className="mb-6 flex items-center justify-end">
            <div className="flex items-center space-x-6">
              <div className="flex items-center space-x-2">
                <TbTrendingUp size={16} className="text-green-600" />
                <span className="text-sm text-gray-600">{t('legend.income')}</span>
              </div>
              <div className="flex items-center space-x-2">
                <TbTrendingDown size={16} className="text-red-600" />
                <span className="text-sm text-gray-600">{t('legend.expenses')}</span>
              </div>
            </div>
          </div>

          {/* Chart */}
          <div className="w-full">
            <DualBarChart
              data={chartData}
              height={300}
              incomeColor="#059669"
              expensesColor="#dc2626"
            />
          </div>
        </>
      )}
    </div>
  );
}

export default IncomeExpensesChart;
